import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ContactQuickActions } from '../components/ContactQuickActions';
import { FacilitySwitcher, pushRecent } from '../components/FacilitySwitcher';
import type { PersonTarget } from '../components/Person';
import { StateMessage } from '../components/StateMessage';
import { TABS, TAB_BODIES, type TabName } from '../components/tabs';
import { api, ApiError } from '../lib/api';
import { applyFilters, DEFAULT_FILTERS, readFilters, writeFilters, type FilterState } from '../lib/filters';
import { usePortfolio } from '../lib/portfolio-context';
import type { FacilityDetail as Detail } from '../lib/types';
import { useTeamClock } from '../lib/use-team-clock';

/** A projection older than this is called out in the footer rather than implied fresh. */
const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * The word the stepper's denominator is counted in, or null when there is
 * nothing to qualify.
 *
 * A bare "1 of 253" reads as the whole portfolio, and it never is:
 * DEFAULT_FILTERS.status is 'Active' and applyFilters drops everything
 * inactive, so even a deep link with no query string at all counts MANAGED
 * facilities only. The Portfolio screen has always been explicit about this
 * ("<n> managed facilities · <n> in archive"); the detail header printed the
 * fraction with no universe attached, so the facilities missing from it read as
 * data loss rather than as a filter doing its job.
 *
 * The status filter is the one nobody can see from this screen, so it gets its
 * own word. Everything else — a search term, a region, a brand, a state, a
 * district manager — was chosen by the person holding the link and is spelled
 * out on the portfolio behind them, so it collapses to "matching" rather than
 * being recited in a header.
 */
function stepperUniverse(f: FilterState, narrowed: boolean): string | null {
  // Named only when the set is genuinely narrower than the master, which is
  // decided by counting rather than by asking whether a filter is SET. A filter
  // that happens to exclude nothing — status 'All', or a region that covers
  // every site — is not worth a word, and the bare fraction is honest there.
  if (!narrowed) return null;
  const NARROWING = ['q', 'region', 'brand', 'state', 'district'] as const;
  if (NARROWING.some((k) => f[k] !== DEFAULT_FILTERS[k])) return 'matching';
  switch (f.status) {
    case 'Active':
      return 'managed';
    case 'Inactive':
      return 'in the archive';
    case 'Unmanned':
      return 'unmanned';
    case 'Vacancy':
      return 'with a manager vacancy';
    default:
      return 'matching';
  }
}

export function FacilityDetail() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data } = usePortfolio();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabName>('General');
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [contact, setContact] = useState<{ target: PersonTarget; anchor: DOMRect } | null>(null);

  // PunchWISE clock status for the On-site team — its OWN call, made only
  // once the page has a facility, and only while the panel that shows it is on
  // screen. The page never waits for it and cannot fail because of it.
  const clock = useTeamClock(
    detail?.facility.id ?? null,
    tab === 'General' && detail?.facility.team.state === 'present',
  );

  const filters = readFilters(params);
  const list = useMemo(
    () => (data ? applyFilters(data.facilities, data.units, filters) : []),
    [data, filters],
  );

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    setContact(null);
    api
      .facility(id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        pushRecent(d.facility.id);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ⌘K opens the switcher, matching the hint in the chrome.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSwitcherOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const qs = params.toString();
  const goTo = (facilityId: string) => navigate(`/facility/${encodeURIComponent(facilityId)}${qs ? `?${qs}` : ''}`);
  const backToPortfolio = () => navigate(`/${qs ? `?${qs}` : ''}`);

  if (error) {
    // 409 — the key matched more than one facility. The candidates are offered
    // as a choice rather than one of them being picked silently.
    if (error.status === 409 && error.candidates?.length) {
      return (
        <StateMessage
          title="That code matches more than one facility"
          body={
            'Yardi codes are not unique in the facility master — some cities have two sites sharing ' +
            'one code. Pick the one you meant; the link you follow will be unambiguous.'
          }
        >
          <div className="fw-cand">
            {error.candidates.map((c) => (
              <Link key={c.id} to={`/facility/${encodeURIComponent(c.id)}${qs ? `?${qs}` : ''}`}>
                <span>
                  <span className="fw-cand-n">{c.name}</span>
                  <span className="fw-cand-s">SiteLink {c.siteLinkCode ?? 'not assigned'}</span>
                </span>
              </Link>
            ))}
          </div>
        </StateMessage>
      );
    }
    return (
      <StateMessage
        title={error.status === 404 ? 'No such facility' : 'Facility could not be loaded'}
        body={
          error.status === 404
            ? 'Nothing in the facility master matches this identifier. It may have been renamed or removed from the source list.'
            : 'The facility master on WPICSQL2502 could not be read.'
        }
        detail={error.status === 404 ? undefined : (error.detail ?? error.message)}
        onRetry={error.status === 404 ? undefined : () => navigate(0)}
      />
    );
  }

  if (!detail) {
    return (
      <div className="fw-center">
        <div className="fw-msg">
          <div className="fw-skel" style={{ width: 220, height: 22 }} />
          <div className="fw-skel" style={{ width: 320, marginTop: 12 }} />
          <div className="fw-skel" style={{ width: 260, marginTop: 8 }} />
        </div>
      </div>
    );
  }

  const x = detail.facility;
  const idx = list.findIndex((f) => f.id === x.id);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
  const Body = TAB_BODIES[tab];

  // The unfiltered master is the yardstick: if the filtered list is shorter,
  // something is being held back and the stepper says what it is counting. The
  // two counts come from the same projection, so nothing here hardcodes a
  // portfolio size — both numbers move with the source.
  const universe = stepperUniverse(filters, !!data && list.length !== data.facilities.length);

  // Reopens THIS facility in a view guaranteed to contain it. The status filter
  // lives in the query string and 'All' is the only value that keeps the
  // archive; every other narrowing filter is reset too, because any one of them
  // can be what dropped the facility. Sort, direction and view are the user's
  // own preferences rather than filters, so they survive. Built through
  // writeFilters so the param spellings stay in exactly one place — filters.ts.
  const showEverything = () =>
    navigate(
      `/facility/${encodeURIComponent(x.id)}?${writeFilters({
        ...DEFAULT_FILTERS,
        sort: filters.sort,
        dir: filters.dir,
        view: filters.view,
        status: 'All',
      }).toString()}`,
    );

  const builtAt = data?.builtAt ? new Date(data.builtAt) : null;
  const stale = builtAt ? Date.now() - builtAt.getTime() > STALE_AFTER_MS : false;

  return (
    <>
      <div className="fhead">
        <div className="fh-inner">
          <div className="fh-nav">
            <button className="backlink" onClick={backToPortfolio} type="button">
              ← Portfolio
            </button>
            {/* The stepper walks the SAME filtered set the user came from, so
                "4 of 37" agrees with the list behind it. */}
            {idx >= 0 && (
              <div className="stepper">
                <button
                  className="step"
                  disabled={!prev}
                  title={prev?.name ?? ''}
                  onClick={() => prev && goTo(prev.id)}
                  type="button"
                  aria-label="Previous facility"
                >
                  ←
                </button>
                <span className="step-n">
                  {/* The denominator is the FILTERED count, never the master —
                      so it names its universe whenever the two differ. */}
                  {idx + 1} of {list.length}
                  {universe ? ` ${universe}` : ''}
                </span>
                <button
                  className="step"
                  disabled={!next}
                  title={next?.name ?? ''}
                  onClick={() => next && goTo(next.id)}
                  type="button"
                  aria-label="Next facility"
                >
                  →
                </button>
              </div>
            )}
            {/* The facility is not in the filtered set at all — usually an
                archived site reached by a bare link, since the default status
                filter is 'Active'. Hiding the stepper is right (a position in a
                list this facility is not in would be a fiction), but hiding it
                SILENTLY made the missing controls look like a fault. Say what
                happened and offer the view that is certain to contain it.
                Gated on `data` as well, because a null projection also yields
                idx -1 — while the portfolio is still loading nothing is known
                about what is or is not in the view, and claiming otherwise
                would be the same silent lie in the other direction. It sits
                inside .stepper to inherit the stepper's placement AND its
                mobile rule: where there is no room for the controls there is no
                room for a sentence about them. */}
            {idx < 0 && data && (
              <div className="stepper">
                <span className="step-n">Not in the current view</span>
                <button className="backlink" onClick={showEverything} type="button">
                  Show everything →
                </button>
              </div>
            )}
          </div>

          <div className="fh-top">
            <div className="fh-l">
              <button className="switcher" onClick={() => setSwitcherOpen((v) => !v)} type="button">
                <h1>{x.name}</h1>
                <span className="sw-caret">▾</span>
              </button>
              {/* Facility_Information.District holds the district MANAGER's
                  NAME, not a district. Everything downstream already knew it —
                  the repository binds District/District_Manager into this
                  field, the portfolio filter says so, the derived tile is
                  called "No district manager", and the General tab renders this
                  very value as "District Manager". Only the label here still
                  called a person a place. There is no district NAME anywhere in
                  the mirror to show instead, so the value was never wrong and
                  the binding does not change — the label does. */}
              <div className="fh-dist">
                District Manager: <b>{x.district ?? <span className="na">Unassigned</span>}</b>
              </div>
              <div className="fh-flags">
                {x.active ? (
                  <span className="chip ok">Active</span>
                ) : (
                  <span className="chip off">Inactive</span>
                )}
                {x.mgmt && <span className="chip">{x.mgmt}</span>}
                {x.region && <span className="chip q">{x.region}</span>}
                {x.manned !== null && <span className="chip q">{x.manned ? 'Manned' : 'Unmanned'}</span>}
              </div>

              {switcherOpen && data && (
                <FacilitySwitcher
                  current={x}
                  facilities={data.facilities}
                  onPick={(f) => {
                    setSwitcherOpen(false);
                    goTo(f.id);
                  }}
                  onClose={() => setSwitcherOpen(false)}
                  onBrowseAll={() => {
                    setSwitcherOpen(false);
                    backToPortfolio();
                  }}
                />
              )}
            </div>

            <div className="fh-r">
              <div className="fcode">{x.code}</div>
            </div>
          </div>

          <div className="tabs">
            {TABS.map((t) => (
              <button
                className={`tab${t === tab ? ' on' : ''}`}
                key={t}
                onClick={() => setTab(t)}
                type="button"
                aria-current={t === tab ? 'page' : undefined}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="content">
        {/* Every tab is a function of the selected facility — no tab holds
            static content. This was a real defect during design, where IT,
            Features and Operations showed one site's data for all eighteen. */}
        <Body detail={detail} clock={clock} onPerson={(target, anchor) => setContact({ target, anchor })} />
      </div>

      <div className="stamp">
        <span>
          {x.modified ? (
            <>
              Last updated {x.modified}
              {x.modifiedBy ? ` by ${x.modifiedBy}` : ''}
            </>
          ) : (
            'No modification date on file'
          )}
        </span>
        <span>
          {builtAt && (
            <span className={stale ? 'fw-stale' : undefined}>
              Source read {builtAt.toLocaleString()} ·{' '}
            </span>
          )}
          FacilityWISE · WISE platform
        </span>
      </div>

      {contact && (
        <ContactQuickActions
          target={contact.target}
          facility={x}
          anchor={contact.anchor}
          onClose={() => setContact(null)}
        />
      )}
    </>
  );
}
